import $ from 'jquery';
import debounce from 'lodash.debounce';
import * as loader from './loader';
import * as suggest from './suggest';
import * as scrollTo from './scrollTo';
import { setSearchParam, searchQueryStringKeys, buildUrl, getQueryText } from './search';
import { MQ } from '../tools/MQ.js';

export const init = ($target) => {
	const $searchFilter = $('.js-search-filter'),
		$searchBox = $searchFilter.find('.js-search-box'),
		$mainResultsBlock = $('#js-main-results');

	if (!$searchBox.length || !$mainResultsBlock.length || !$searchFilter.length) {
		return;
	}

	///////////////////////////////////////////////////////////////////////////////
	/// CONSTANTS AND VARIABLES
	///////////////////////////////////////////////////////////////////////////////

	const MAIN_RESULTS_COUNT = 3,
		QUERY_MIN_INPUT_LENGTH = 3,
		FADE_DURATION_MS = 300,
		DEBOUNCE_WAIT_MS = 500,
		TAXONOMY_WIKI = 'Wiki',
		TAXONOMY_CARE = 'Care',
		API_BASE_URL = $searchBox.attr('action'),
		SUGGEST_BASE_URL = $searchBox.data('suggest-base-url');

	const $queryTextInput = $searchBox.find('input[name=queryText]'),
		$searchHeaderTerm = $('.search-header .search-header__term-query'),
		$moreResultsBlock = $('#js-more-results'),
		$segmentRadios = $('.js-segments .search-filter__nested-item input[type=radio][name=segment]'),
		$infoTypeBlocks = $('.js-info-types'),
		$infoTypeCheckboxes = $infoTypeBlocks.find('.search-filter__nested-item input[type=checkbox][name=categories]'),
		$infoCategoriesBlocks = $('.js-info-categories'),
		$infoCategoriesCheckboxes = $infoCategoriesBlocks.find('.search-filter__nested-item input[type=checkbox][name=categories]'),
		$queryErrorMessage = $('.js-query-error-message'),
		$loader = $('.js-loader'),
		emptyFieldErrorMessage = $queryErrorMessage.data('empty-field-message'),
		minLengthErrorMessage = $queryErrorMessage.data('min-length-message');

	///////////////////////////////////////////////////////////////////////////////
	/// INITIALIZATION
	///////////////////////////////////////////////////////////////////////////////

	$target.ready(async function () {
		resetFilter();

		await attachCallbacks();

		initFilterValues(new URLSearchParams(window.location.search));
	});

	/**
	 * Initializes filter values based on URL search params.
	 * @param {URLSearchParams} urlSearchParams
	 */
	function initFilterValues(urlSearchParams) {
		const queryText = urlSearchParams.get(searchQueryStringKeys.queryText)?.trim(),
			categories = urlSearchParams.getAll(searchQueryStringKeys.categories).join(),
			segment = urlSearchParams.get(searchQueryStringKeys.segment) ?? $('.js-segments').data('default-segment');

		// text input
		$queryTextInput.val(queryText);
		if (!queryText || queryText.length < QUERY_MIN_INPUT_LENGTH) {
			$queryTextInput.trigger('focus');
		}

		// segment
		let preventSubmit = categories || !urlSearchParams.has(searchQueryStringKeys.segment);
		let preventQueryUpdate = true;
		selectSegment(segment, preventSubmit, preventQueryUpdate);

		// categories
		if (categories && categories.length) {
			preventSubmit = false;
			preventQueryUpdate = true;
			selectCategories(categories.split(','), preventSubmit, preventQueryUpdate);
		}
	}

	async function attachCallbacks() {
		attachOnPopStateCallback();
		attachSearchBoxCallbacks();
		attachSegmentRadiosCallbacks();
		attachCheckboxCallbacks($infoTypeCheckboxes, $infoCategoriesBlocks);
		attachCheckboxCallbacks($infoCategoriesCheckboxes);
	}

	/**
	 * Previous page callback.
	 */
	function attachOnPopStateCallback() {
		window.onpopstate = function (event) {
			initFilterValues(new URLSearchParams(event.target.location.search));
		};
	}

	/**
	 * Search box includes the input field and the submit button.
	 */
	function attachSearchBoxCallbacks() {
		$searchBox.on('submit', function (event) {
			event.preventDefault();

			onSubmit(false);
		});

		$queryTextInput.on('input', function () {
			const removeErrorBorder = getQueryText($queryTextInput).length >= QUERY_MIN_INPUT_LENGTH;
			hideErrorMessage(removeErrorBorder);
		});

		$queryTextInput.on('input', debounce(onInput, DEBOUNCE_WAIT_MS));
	}

	/**
	 * Radio buttons callbacks.
	 */
	function attachSegmentRadiosCallbacks() {
		$segmentRadios.each(function () {
			const $segmentRadio = $(this),
				segment = $segmentRadio.val();

			$segmentRadio.on('change', function (event, preventSubmit, preventQueryUpdate) {
				// reset all values
				resetFilter();

				if (segment && segment.length) {
					// toggle info type block visibility
					$infoTypeBlocks.each(function () {
						if ($(this).attr('id') === segment) {
							$(this).fadeIn(FADE_DURATION_MS);
						} else {
							$(this).fadeOut(FADE_DURATION_MS);
						}
					});
				}

				// submit
				if (!preventSubmit) onSubmit(preventQueryUpdate);
			});
		});
	}

	/**
	 * Checkbox callbacks.
	 * @param {*} $checkboxes
	 * @param {*} $targetBlock Target block to fade in on checked.
	 */
	function attachCheckboxCallbacks($checkboxes, $targetBlock = null) {
		// if no target block is bound, just submit
		if (!$targetBlock) {
			$checkboxes.on('change', function (event, preventSubmit, preventQueryUpdate) {
				if (!preventSubmit) onSubmit(preventQueryUpdate);
			});

			return;
		}

		// toggle target block visibility
		$checkboxes.each(function () {
			const $checkbox = $(this),
				targetBlockId = $checkbox.val();

			if (!targetBlockId) {
				return;
			}

			const targetBlock = $targetBlock
				.filter(function () {
					return $(this).attr('id') === targetBlockId;
				})
				.first();

			$checkbox.on('change', function (event, preventSubmit, preventQueryUpdate) {
				if (this.checked) {
					targetBlock.fadeIn(FADE_DURATION_MS);
				} else {
					targetBlock.fadeOut(FADE_DURATION_MS);

					// reset checkboxes within target block
					const targetBlockCheckboxes = targetBlock.find('input[type=checkbox]');
					targetBlockCheckboxes.prop('checked', false);
				}

				if (!preventSubmit) onSubmit(preventQueryUpdate);
			});
		});
	}

	///////////////////////////////////////////////////////////////////////////////
	/// SEARCH EVENTS
	///////////////////////////////////////////////////////////////////////////////

	function onInput() {
		const params = getParams($searchFilter);

		let url = `${SUGGEST_BASE_URL}/${encodeURIComponent(params.queryText)}/${params.segment}`;

		let urlSearchParams = new URLSearchParams();
		setSearchParam(urlSearchParams, searchQueryStringKeys.categories, params.categories);

		url = buildUrl(url, urlSearchParams);

		suggest.onInput($queryTextInput, url);
	}

	async function onSubmit(preventQueryUpdate) {
		const params = getParams($searchFilter);

		// Hledaný výraz „{queryText}“
		$searchHeaderTerm.text(params.queryText);

		if (!params.queryText || params.queryText.length < QUERY_MIN_INPUT_LENGTH) {
			fillSearchResults({});
			loader.hide($loader);

			const errorMessage = params.queryText.length === 0 ? emptyFieldErrorMessage : minLengthErrorMessage;
			showErrorMessage(errorMessage);

			return;
		}

		hideErrorMessage();
		$queryTextInput.trigger('blur');
		loader.show($loader);

		if (!preventQueryUpdate) updateQueryString(params);

		let response = '';
		try {
			response = await search(params);
		} catch (err) {
			if (err.status !== '404') console.error('There seems to be an error with the API');
		}

		fillSearchResults(response);
		loader.hide($loader);

		// scroll to results (mobile only)
		if (MQ('mdDown')) scrollTo.scrollToId('#js-main-results');

		try {
			pushDataLayer(params, response);
		} catch (err) {
			console.error('Could not push data layer: ' + err);
		}
	}

	function search(params) {
		let url = `${API_BASE_URL}/${params.segment}`;

		let urlSearchParams = new URLSearchParams();
		setSearchParam(urlSearchParams, searchQueryStringKeys.queryText, params.queryText);
		setSearchParam(urlSearchParams, searchQueryStringKeys.categories, params.categories);

		url = buildUrl(url, urlSearchParams);

		return $.ajax({
			type: 'GET',
			url: url,
			contentType: 'text/plain',
		});
	}

	function pushDataLayer(params, response) {
		const getCheckedCategoriesByTaxonomy = ($searchFilter, taxonomy) =>
			$searchFilter
				.find('.js-info-categories')
				.filter(function () {
					return $(this).data('taxonomy') === taxonomy;
				})
				.find('input[type=checkbox]:checked');

		const checkbox2name = (checkboxes) => checkboxes.get().map((checkbox) => $(checkbox).next().text());

		if (!response || !response.results) {
			return;
		}

		const checkedInfoTypeCheckboxes = $searchFilter.find('.js-info-types input[type=checkbox]:checked');
		const checkedSegmentRadio = $searchFilter.find('.js-segments input[type=radio]:checked');
		const checkedCareCategories = getCheckedCategoriesByTaxonomy($searchFilter, TAXONOMY_CARE);
		const checkedWikiCategories = getCheckedCategoriesByTaxonomy($searchFilter, TAXONOMY_WIKI);

		const dataLayerObject = {
			event: 'internalSearchResult',
			internalSearchType: $searchBox.attr('id'),
			internalSearchKeyword: params.queryText,
			internalSearchResultCount: response.results.length,
			internalSearchFilters: {
				segments: checkbox2name(checkedSegmentRadio),
				types: checkbox2name(checkedInfoTypeCheckboxes),
				categoriesCare: checkbox2name(checkedCareCategories),
				categoriesWiki: checkbox2name(checkedWikiCategories),
			},
		};

		dataLayer.push(dataLayerObject);
	}

	///////////////////////////////////////////////////////////////////////////////
	/// UTILS
	///////////////////////////////////////////////////////////////////////////////

	function updateQueryString(params) {
		const urlSearchParams = new URLSearchParams(window.location.search);
		const oldQueryString = urlSearchParams.toString();

		setSearchParam(urlSearchParams, searchQueryStringKeys.queryText, params.queryText);
		setSearchParam(urlSearchParams, searchQueryStringKeys.segment, params.segment);
		setSearchParam(urlSearchParams, searchQueryStringKeys.categories, params.categories);

		const newQueryString = urlSearchParams.toString();

		if (oldQueryString === newQueryString) {
			return;
		}

		window.history.pushState(null, '', '?' + newQueryString);
	}

	function fillSearchResults(response) {
		let mainResultsHtml = '',
			moreResultsHtml = '';

		if (!response || !response.results || !response.results.length) {
			mainResultsHtml = getNoResultsItemHtml();
		} else {
			mainResultsHtml = getSearchResultListHtml(response.results.slice(0, MAIN_RESULTS_COUNT), true);
			moreResultsHtml = getSearchResultListHtml(response.results.slice(MAIN_RESULTS_COUNT), false);
		}

		fillBlock($mainResultsBlock, mainResultsHtml);

		if (moreResultsHtml.length) {
			fillBlock($moreResultsBlock, moreResultsHtml);
		} else {
			clearMoreResults();
		}
	}

	function fillBlock($block, html) {
		$block.find('.search-result__list').html(html);
		$block.removeClass('search-result__wrap--hidden');
	}

	function clearMoreResults() {
		$moreResultsBlock.addClass('search-result__wrap--hidden');
		$moreResultsBlock.find('.search-result__list').empty();
	}

	function showErrorMessage(message) {
		if (!$searchBox.hasClass('has-error')) $searchBox.addClass('has-error');
		$queryErrorMessage.find('.item-icon__text').text(message);
		$queryErrorMessage.removeClass('u-hide');
	}

	function hideErrorMessage(removeErrorBorder = true) {
		$queryErrorMessage.addClass('u-hide');
		if (removeErrorBorder) $searchBox.removeClass('has-error');
	}

	/**
	 * Selects a segment based on segment code name
	 * @param {string} segmentCodeName
	 * @param {boolean} preventSubmit
	 * @param {boolean} preventQueryUpdate
	 */
	function selectSegment(segmentCodeName, preventSubmit = false, preventQueryUpdate = false) {
		$segmentRadios
			.filter(function () {
				const radioSegmentCodeName = $(this).val();

				return radioSegmentCodeName && radioSegmentCodeName.includes(segmentCodeName);
			})
			.first()
			.prop('checked', true)
			.trigger('change', [preventSubmit, preventQueryUpdate]);
	}

	/**
	 * Selects categories checkboxes based on an array of category node alias paths.
	 * @param {string[]} categories
	 * @param {boolean} preventSubmit
	 * @param {boolean} preventQueryUpdate
	 */
	async function selectCategories(categories, preventSubmit = false, preventQueryUpdate = false) {
		const selectVisibleCheckboxes = async ($blocks, categories) => {
			$blocks
				.filter(':visible')
				.find('input[type=checkbox][name=categories]')
				.filter(function () {
					let nodeAliasPaths = $(this).val();

					if (!nodeAliasPaths) {
						return false;
					}

					nodeAliasPaths = nodeAliasPaths.split(',');
					return nodeAliasPaths.filter((nodeAliasPath) => categories.includes(nodeAliasPath)).length > 0;
				})
				.prop('checked', true)
				.trigger('change', [true, true]);
		};

		await selectVisibleCheckboxes($infoTypeBlocks, categories);
		await selectVisibleCheckboxes($infoCategoriesBlocks, categories);

		if (!preventSubmit) onSubmit(preventQueryUpdate);
	}

	function getParams() {
		return {
			queryText: getQueryText($queryTextInput),
			segment: $searchFilter.find('.js-segments input[type=radio][name=segment]:checked').val(),
			categories: getCheckedCategories(),
		};
	}

	function getCheckedCategories() {
		const $checkedInputs = $searchFilter.find('input[type=checkbox]:checked');
		return $checkedInputs
			.map((idx, item) => $(item).val())
			.get()
			.join();
	}

	function resetFilter() {
		hideErrorMessage();
		$infoTypeBlocks.hide();
		$infoTypeCheckboxes.prop('checked', false);
		$infoCategoriesBlocks.hide();
		$infoCategoriesCheckboxes.prop('checked', false);
	}

	const getSearchResultListHtml = (results, showAnnotation) =>
		results.map((result) => getSearchResultItemHtml(result, showAnnotation)).join('');

	const getSearchResultItemHtml = (result, showAnnotation) => {
		const text =
			!result.text || !showAnnotation
				? ''
				: `
				<span class="search-result__annot">
					${result.text}
				</span>`;

		return `
			<li class="search-result__item">
				${getBreadCrumbListHtml(result.breadCrumbs)}
				<a href="${result.link}" ${result.openInNewTab ? 'target="_blank" rel="noopener"' : ''} class="search-result__link">
					<span class="item-icon item-icon--after item-icon--space-between">
						<span class="item-icon__icon">${arrowRight}</span>
						<span class="item-icon__text">
							<span class="search-result__title">${result.pageName}</span>
						</span>
					</span>
					${text}
				</a>
			</li>`;
	};

	const getBreadCrumbListHtml = (breadCrumbs) => `
		<nav class="breadcrumb" aria-label="breadcrumb">
			<ol class="breadcrumb__list">
				${breadCrumbs
					.map(
						(breadCrumb, i) => `
					<li class="breadcrumb__item">
						<a href="${breadCrumb.url}" class="breadcrumb__link" ${i === breadCrumbs.length - 1 ? 'aria-current="page"' : ''}>${breadCrumb.name}</a>
						<span class="breadcrumb__separator">
							${i !== breadCrumbs.length - 1 ? arrowRight : ''}
						</span>
					</li>`,
					)
					.join('')}
			</ol>
		</nav>`;

	const arrowRight = `
		<span class="icon-svg icon-svg--arrow-right" aria-hidden="true">
			<svg class="icon-svg__svg" xmlns:xlink="http://www.w3.org/1999/xlink">
				<use xlink:href="/img/bg/icons-svg.svg#icon-arrow-right" width="100%" height="100%" focusable="false"></use>
			</svg>
		</span>`;

	const getNoResultsItemHtml = () => {
		const text = $mainResultsBlock.data('no-results-text');
		return `
			<li class="search-result__item">
				<span class="search-result__title">${text}</span>
			</li>`;
	};
};
